import {
  SET_GAME_ID,
  SET_TITLE,
  SET_TRANSLATE_DATA,
  UPDATE_LANG_LIST,
  SET_SURVEY_START_STATE,
  SET_SURVEY_END_STATE
} from "./mutations";
import { handleQuestionList } from "@/hooks/shuffleHook";
import {
  collectInfo,
  commonAnswerInfo,
  singleEditInfo,
} from "../../../hooks/answerHook";
import {
  loadAnswerStartTime,
  loadQuestionList,
  loadResultList,
  saveAnswerStartTime,
  saveQuestionList,
  saveResultList,
  saveSurveyEditTime,
} from "../../../hooks/historyHook";
import { sendTypeMap } from "../../../hooks/defMap";
import { resultState } from "../../../hooks/resultHook";
import { useDateFormat, useNow } from "@vueuse/core";
import { getLanguageList, getSurvey } from "@/api/survey";
import { nowLang, language } from "@/language";

export const SET_SURVEY_INFO = "SET_SURVEY_INFO";
export const BACK_PREV_QUESTION = "BACK_PREV_QUESTION";
export const GO_NEXT_QUESTION = "GO_NEXT_QUESTION";
export const COMMIT_SURVEY_INFO = "COMMIT_SURVEY_INFO";
export const RECOVER_HISTORY = "RECOVER_HISTORY";
export const START_SURVEY = "START_SURVEY";
export const INIT_LANGUAGE_LIST = "INIT_LANGUAGE_LIST";
export const FETCH_SURVEY = "FETCH_SURVEY";
export const actions = {
  [SET_SURVEY_INFO]({ commit, state }, data) {
    if (!state.firstRequest) {
      // 非首次请求问卷接口，（切换语言）仅更新翻译id
      commit(SET_TRANSLATE_DATA, data.translate);
      commit(SET_TITLE, data.config.TITLE);
      return;
    }
    let newQuestionList = handleQuestionList(
      data.question?.JSON_LIST?.questionList || []
    );
    saveQuestionList(newQuestionList); // 缓存题目列表
    state.questionFlag = data.config.QUESTION_FLAG;
    state.isAnswer = data.isAnswer;
    state.isAward = data.isAward;
    state.activityStartTime = data.config.ACTIVITY_START_TIME;
    state.activityEndTime = data.config.ACTIVITY_END_TIME;
    state.coverAwardList = data.config.AWARD_PIC_LIST || [];
    state.frontPicType = data.config.FRONT_PIC;
    state.hideLogin = data.config.NEED_HIDE_LOGIN;
    if (data.config.COVER_PIC_LIST?.type_h) {
      state.coverPicList.type_h = data.config.COVER_PIC_LIST.type_h;
    }
    if (data.config.COVER_PIC_LIST?.type_v) {
      state.coverPicList.type_v = data.config.COVER_PIC_LIST.type_v;
    }
    state.coverFontColor = data.config.COVER_FONT_COLOR || "#fff";
    state.loginLimit = data.config.LOGIN_LIMIT;
    state.latestEditTime = data.question.EDIT_TIME;
    state.sendType = data.config.SEND_TYPE;
    state.cover = data.question.JSON_LIST?.prePageConfig;
    state.finishConfig = data.question.JSON_LIST?.finishConfig;
    state.questionList = newQuestionList;
    state.config = data.config;
    state.gameName = data.config.GAME_NAME;
    state.firstRequest = false;
    state.loadingState = false;
    commit(SET_TITLE, data.config.TITLE);
    commit(SET_TRANSLATE_DATA, data.translate);
    commit(SET_GAME_ID, data.config.GAME_ID);
    // 处理结果页，判断是否为优惠券发奖
    resultState.isCoupon =
      +state.config.SEND_TYPE === 0 &&
      state.config.SEND_VT_CONFIG[0][0]["vtType"] === "coupon";
    resultState.showType = resultState.isCoupon
      ? "common"
      : sendTypeMap[data.config.SEND_TYPE];
  },
  [START_SURVEY]({ state, getters }) {
    // 进入第一题
    state.currentIndex = 0;
    state.surveyStartTime = new Date();
    saveSurveyEditTime(state.latestEditTime); // 缓存问卷最新编辑时间
    saveAnswerStartTime(useDateFormat(useNow(), "YYYY-MM-DD HH:mm:ss").value); // 缓存开始问卷时间
    // 开始问卷打点
    window.surveyDataLayer.push({
      eventName: "start",
      answerStartTime: loadAnswerStartTime(),
    });
    // 玩家行为打点：首题
    window.surveyDataLayer.push({
      eventName: "enter_question",
      answerStartTime: loadAnswerStartTime(),
      questionNumber: 1,
      questionType: getters.currentQuestionType,
    });
  },
  [BACK_PREV_QUESTION]({ state, getters }, prevQuestionIndex) {
    // 根据下标拿到上一题的作答数据
    let prevResult = state.resultList[prevQuestionIndex];
    // 渲染数据
    state.chooseAnswer = prevResult.chooseAnswerIndexArray;
    state.matrixAnswer = prevResult.matrixAnswerIndexArray;
    prevResult.resultType === "common" &&
      (commonAnswerInfo.value = prevResult.answerChoose);
    prevResult.resultType === "cloze" &&
      (singleEditInfo.value = prevResult.answerSingleEdit);
    if (prevResult.resultType === "messageCollect") {
      if (prevResult.answerCollectInfo.phoneArea) {
        let [code, area] = prevResult.answerCollectInfo.phoneArea.split("+");
        prevResult.answerCollectInfo.phoneArea = area;
        prevResult.answerCollectInfo.phone =
          prevResult.answerCollectInfo.phone.slice(area.length);
      }
      collectInfo.value = prevResult.answerCollectInfo;
    }
    state.resultList.length = prevQuestionIndex;
    saveResultList(state.resultList);
    // 更改当前下标回到上一题
    window.surveyDataLayer.push({
      eventName: "back_prev_question",
      answerStartTime: loadAnswerStartTime(),
      questionNumber: state.currentIndex + 1,
      previousQuestionNumber: prevQuestionIndex + 1,
      questionType: getters.getQuestionTypeByQuestionIndex(prevQuestionIndex),
    });
    state.currentIndex = prevQuestionIndex;
  },
  [GO_NEXT_QUESTION]({ state, getters }, nextQuestionIndex) {
    // 如果存在跳题（无条件跳、按选项跳、关联题目未激活），则填充空对象
    const nextValidIndex = getters.getValidQuestionIndex(nextQuestionIndex);
    if (state.currentIndex !== nextValidIndex - 1) {
      let jumpCount = nextValidIndex - state.currentIndex - 1;
      for (let i = 0; i < jumpCount; i++) {
        state.resultList.push({});
      }
    }
    // 玩家行为打点：完成上一题作答，传入上一题的序号
    window.surveyDataLayer.push({
      eventName: "answer_question",
      answerStartTime: loadAnswerStartTime(),
      questionNumber: state.currentIndex + 1,
      nextQuestionNumber: nextValidIndex + 1,
      questionType: getters.currentQuestionType,
      duration: new Date() - state.enterQuestionTime,
    });
    // 玩家行为打点：即将进入下一题，传入下一题的序号
    window.surveyDataLayer.push({
      eventName: "enter_question",
      answerStartTime: loadAnswerStartTime(),
      questionNumber: nextValidIndex + 1,
      questionType: getters.getQuestionTypeByQuestionIndex(nextValidIndex),
    });
    // 更改下标前往下一题,判断是否激活
    state.currentIndex = getters.getValidQuestionIndex(nextQuestionIndex);
  },
  [RECOVER_HISTORY]({ state, getters }) {
    let localResultList = JSON.parse(loadResultList());
    state.questionList = JSON.parse(loadQuestionList());
    state.resultList = localResultList;
    setTimeout(() => {
      let lastIndex = getters.getValidQuestionIndex(
        localResultList.slice(-1)[0].nextQuestionIndex
      );
      let jumpCount = lastIndex - localResultList.length;
      for (let i = 0; i < jumpCount; i++) {
        // 填充空对象
        state.resultList.push({});
      }
      state.currentIndex = localResultList.length;
      // 玩家行为打点：前往上一次作答题号
      window.surveyDataLayer.push({
        eventName: "enter_question",
        answerStartTime: loadAnswerStartTime(),
        questionNumber: state.currentIndex + 1,
        questionType: getters.currentQuestionType,
      });
    }, 20);
  },
  // 初始化语言列表
  async [INIT_LANGUAGE_LIST]({ commit }) {
    try {
      const res = await getLanguageList();
      if (+res.result === 1 && res.data) {
        const codeList = res.data.map((item) => item.code);
        if (codeList.length) {
          // 检测当前语言是否包含在语言列表中
          const includeLang = codeList.includes(nowLang.value);
          if (!includeLang) {
            language.setLang(codeList[0]);
          }
        }
        commit(UPDATE_LANG_LIST, res.data);
      }
    } catch (error) {
      console.error("获取语言列表失败:", error);
    }
  },
  // 获取问卷信息
  async [FETCH_SURVEY]({ commit, dispatch, state }, language) {
    try {
      const surveyRes = await getSurvey(language);
      if (+surveyRes.result === 1) {
        // 成功获取问卷信息，设置到 store
        await dispatch(SET_SURVEY_INFO, surveyRes.data);
        return {
          success: true,
          data: surveyRes.data,
        };
      } else {
        // 处理问卷状态错误
        state.loadingState = false;
        const code = surveyRes.code;
        if (+code === -20006) {
          commit(SET_SURVEY_START_STATE, false);
        } else if (+code === -20007) {
          commit(SET_SURVEY_END_STATE, true);
        }
        return {
          success: false,
          code,
        };
      }
    } catch (error) {
      console.error("获取问卷信息失败:", error);
      return {
        success: false,
        error,
      };
    }
  },
};
